import React, { useEffect, useRef, useState } from "react";
import { Send, Check } from "lucide-react";
import { api } from "@/lib/api";
import { apiErrorCode } from "../../lib/apiErrors";
import { useLanguage } from "@/lib/LanguageContext";
import { useSiteCopy } from "@/lib/useSiteCopy";

/**
 * ContactForm — the public site's ONE contact form, for all six themes.
 *
 * CR-T-141 (owner decision, 2026-09-30): on a practice's OWN public site, the Contact
 * section no longer opens a thread in the practice's Messages inbox — it shows the
 * practice's CRM CAPTURE FORM, the same lead-capture form the owner configures at
 * `/admin/crm/forms` and embeds on external sites (`modules/crm/routers/public.py`'s
 * `_EMBED_JS`). Messaging moved to a separate floating chat widget, which is not this
 * component's concern.
 *
 * WHAT THIS COMPONENT IS: a native React re-implementation of that SAME two-step widget
 * (name/email/phone?/message?/consent → "Send code" → 6-digit code → "Submit"), reading
 * its configuration from `GET /public/crm/site-form` (same-origin, host-resolved tenant
 * — `backend/modules/crm/routers/public.py::public_site_form`, NOT the foreign-origin,
 * wide-open-CORS `/public/crm/forms/{form_key}` prefix the `<script>` embed uses) and
 * writing through the SAME two write routes the embed does:
 * `POST /public/crm/forms/{form_key}/verification` (email-ownership code) and
 * `POST /public/crm/forms/{form_key}/submissions` (the actual lead). No `<script>`
 * injection, no shadow DOM: this is a first-class React tree, styled by the caller's
 * `classNames` slots exactly like every other component on these six themes.
 *
 * LANGUAGE LAW. Every string a visitor reads while filling the form — every placeholder,
 * every button, every error sentence, the consent tick's own text — comes from the
 * backend's locale layer (`cfg.ui`, `modules/crm/locale.capture_widget_strings`;
 * `cfg.consent_text`), the SAME dictionary the foreign-origin embed draws from, so the
 * two surfaces can never say something different for the identical form. The ONE
 * exception is `siteCopy.contact.loadError` (`lib/content.js`): shown when the config
 * fetch itself fails, before any `cfg` — and therefore any `cfg.ui` — exists to read a
 * string from.
 *
 * PRESENTATION-AGNOSTIC, same contract the retired messaging-turn version had: `as`,
 * `className`, `classNames` (grid/field/fieldWide/label/input/textarea/hint/actions/
 * submit/success/error), `rows`, `icon`, `successIcon`, `showLabels`, and any other prop
 * spread onto the form element. `classNames.input` was already declared by all six call
 * sites even while the retired form had no text/email inputs to hang it on (see this
 * file's own git history) — exactly so a form that grows fields again would not need the
 * six theme files touched, which is why none of them needed to change for this rewrite.
 *
 * @param {React.ElementType} [as="form"]  element/component rendered as the form
 * @param {string} [className]             classes on the form element itself
 * @param {object} [classNames]            per-slot classes: grid, field, fieldWide,
 *   label, input, textarea, hint, actions, submit, success, error. `fieldWide` wraps a
 *   full-width row (message, consent) and falls back to `field` when a theme does not
 *   need the distinction.
 * @param {number} [rows=4]                message textarea rows
 * @param {React.ReactNode} [icon]         node after a step's primary action label
 *   (null = none)
 * @param {React.ReactNode} [successIcon]  node shown next to the final "thank you"
 * @param {boolean} [showLabels=true]      false = labels become the inputs' placeholders,
 *   for themes whose forms have no visible labels (still labelled for assistive
 *   technology through `aria-label`)
 * Any other prop is spread on the form element (animation props, data attributes…).
 */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** `{token}` merge substitution — mirrors `_EMBED_JS`'s own `fill()` exactly. */
function fill(tpl, vars) {
  let out = tpl || "";
  for (const k of Object.keys(vars || {})) {
    out = out.split(`{${k}}`).join(vars[k]);
  }
  return out;
}

export default function ContactForm({
  as: Form = "form",
  className = undefined,
  classNames = {},
  rows = 4,
  icon = <Send size={16} />,
  successIcon = <Check size={16} />,
  showLabels = true,
  ...rest
}) {
  const { lang } = useLanguage();
  const siteCopy = useSiteCopy();
  const contact = siteCopy.contact;

  const [cfg, setCfg] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);
  // The visitor's OWN dwell time on the form, mirroring `_EMBED_JS`'s `mountedAt` — set
  // once, at mount, independent of when the config itself finishes loading.
  const mountedAtRef = useRef(Date.now());

  const [step, setStep] = useState("fields"); // fields | code | success
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [consent, setConsent] = useState(false);
  const [code, setCode] = useState("");
  const [website, setWebsite] = useState(""); // honeypot — a real visitor never fills this
  const [sending, setSending] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const cooldownTimerRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/public/crm/site-form", { params: { locale: lang } })
      .then(({ data }) => { if (!cancelled) setCfg(data); })
      .catch(() => { if (!cancelled) setLoadFailed(true); });
    return () => { cancelled = true; };
  }, [lang]);

  // The cooldown's interval is the one timer this component owns — cleared on unmount
  // so a visitor who navigates away mid-cooldown leaves nothing ticking in the background.
  useEffect(() => () => {
    if (cooldownTimerRef.current) clearInterval(cooldownTimerRef.current);
  }, []);

  const stopCooldown = () => {
    if (cooldownTimerRef.current) {
      clearInterval(cooldownTimerRef.current);
      cooldownTimerRef.current = null;
    }
  };

  const startCooldown = (seconds) => {
    stopCooldown();
    setCooldown(seconds);
    cooldownTimerRef.current = setInterval(() => {
      setCooldown((s) => {
        if (s <= 1) {
          stopCooldown();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  };

  const ui = cfg?.ui || {};

  /** 429 first (the ONE refusal whose cause the visitor can act on), else the code → ui
   * mapping the caller supplies, else the generic sentence. */
  const messageFor = (err, byCode) => {
    if (err?.response?.status === 429) return ui.error_rate_limited || "";
    const code_ = apiErrorCode(err);
    return (code_ && byCode[code_]) || ui.error_generic || "";
  };

  const requestCode = async () => {
    setErrorMsg("");
    if (!name.trim() || !email.trim() || !EMAIL_RE.test(email.trim())) {
      setErrorMsg(ui.error_email_invalid || "");
      return;
    }
    setSending(true);
    try {
      await api.post(`/public/crm/forms/${cfg.form_key}/verification`,
        { email: email.trim(), locale: cfg.locale });
      setSending(false);
      setStep("code");
      startCooldown(cfg.resend_cooldown_s || 60);
    } catch (err) {
      setSending(false);
      setErrorMsg(messageFor(err, { EMAIL_INVALID: ui.error_email_invalid }));
    }
  };

  const onRequestCode = (e) => {
    e.preventDefault();
    requestCode();
  };

  const onResend = () => {
    if (cooldown > 0 || sending) return;
    requestCode();
  };

  const onChangeEmail = () => {
    stopCooldown();
    setCooldown(0);
    setCode("");
    setErrorMsg("");
    setStep("fields");
  };

  const onSubmitCode = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSending(true);
    try {
      await api.post(`/public/crm/forms/${cfg.form_key}/submissions`, {
        name: name.trim(), email: email.trim(), code: code.trim(),
        phone: cfg.fields?.phone ? phone : undefined,
        message: cfg.fields?.message ? message : undefined,
        consent, website, elapsed_ms: Date.now() - mountedAtRef.current,
      });
      setSending(false);
      stopCooldown();
      setStep("success");
    } catch (err) {
      setSending(false);
      setErrorMsg(messageFor(err, {
        EMAIL_CODE_INVALID: ui.error_code_invalid,
        EMAIL_CODE_EXPIRED: ui.error_code_expired,
        EMAIL_CODE_TOO_MANY_ATTEMPTS: ui.error_code_too_many_attempts,
        EMAIL_INVALID: ui.error_email_invalid,
      }));
    }
  };

  // `undefined` rather than "" so an unstyled slot renders NO class attribute at all.
  const cx = (slot) => classNames[slot] || undefined;
  // A theme that hides the labels still has to name the field for a screen reader; a
  // placeholder is not an accessible name.
  const fieldProps = (label) => (showLabels ? {} : { placeholder: label, "aria-label": label });

  if (loadFailed) {
    return (
      <Form data-testid="contact-form" className={className} {...rest}>
        <p data-testid="contact-error" className={cx("error")}>{contact.loadError}</p>
      </Form>
    );
  }

  if (!cfg) {
    // Briefly empty while the config loads — not a failure, nothing to announce yet.
    return <Form data-testid="contact-form" className={className} {...rest} />;
  }

  const consentText = fill(
    (cfg.consent_text && (cfg.consent_text[cfg.locale] || cfg.consent_text.en)) || "",
    { practice_name: cfg.practice_name || "" },
  );

  return (
    <Form
      onSubmit={step === "fields" ? onRequestCode : onSubmitCode}
      data-testid="contact-form"
      className={className}
      {...rest}
    >
      {step !== "success" && (
        <div className={cx("grid")}>
          {step === "fields" && (
            <>
              <div className={cx("field")}>
                {showLabels && <label className={cx("label")}>{ui.name_placeholder}</label>}
                <input
                  required
                  data-testid="contact-name"
                  className={cx("input")}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  {...fieldProps(ui.name_placeholder)}
                />
              </div>
              <div className={cx("field")}>
                {showLabels && <label className={cx("label")}>{ui.email_placeholder}</label>}
                <input
                  required
                  type="email"
                  data-testid="contact-email"
                  className={cx("input")}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  {...fieldProps(ui.email_placeholder)}
                />
              </div>
              {cfg.fields?.phone && (
                <div className={cx("field")}>
                  {showLabels && <label className={cx("label")}>{ui.phone_placeholder}</label>}
                  <input
                    data-testid="contact-phone"
                    className={cx("input")}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    {...fieldProps(ui.phone_placeholder)}
                  />
                </div>
              )}
              {cfg.fields?.message && (
                <div className={cx("fieldWide") || cx("field")}>
                  {showLabels && <label className={cx("label")}>{ui.message_placeholder}</label>}
                  <textarea
                    rows={rows}
                    data-testid="contact-message"
                    className={cx("textarea")}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    {...fieldProps(ui.message_placeholder)}
                  />
                </div>
              )}
              <div className={cx("fieldWide") || cx("field")}>
                <label className={cx("hint")}>
                  <input
                    type="checkbox"
                    data-testid="contact-consent"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                  />
                  {" "}{consentText}
                </label>
                {/* Honeypot: a real visitor never sees or fills this field. */}
                <input
                  type="text"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  style={{ position: "absolute", left: "-9999px" }}
                />
              </div>
            </>
          )}
          {step === "code" && (
            <div className={cx("fieldWide") || cx("field")}>
              <p className={cx("hint")}>{fill(ui.code_sent_to, { email: email.trim() })}</p>
              {showLabels && <label className={cx("label")}>{ui.code_placeholder}</label>}
              <input
                required
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                data-testid="contact-code"
                className={cx("input")}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                {...fieldProps(ui.code_placeholder)}
              />
            </div>
          )}
        </div>
      )}

      <div className={cx("actions")}>
        {step === "fields" && (
          <button type="submit" disabled={sending} data-testid="contact-send-code" className={cx("submit")}>
            {sending ? ui.sending : ui.send_code_button}{icon}
          </button>
        )}
        {step === "code" && (
          <>
            <button type="submit" disabled={sending} data-testid="contact-submit" className={cx("submit")}>
              {sending ? ui.submitting : ui.submit_button}{icon}
            </button>
            <button
              type="button"
              data-testid="contact-resend"
              disabled={cooldown > 0 || sending}
              onClick={onResend}
            >
              {cooldown > 0 ? fill(ui.resend_cooldown, { seconds: String(cooldown) }) : ui.resend_button}
            </button>
            <button type="button" data-testid="contact-change-email" onClick={onChangeEmail}>
              {ui.change_email_button}
            </button>
          </>
        )}
        {step === "success" && (
          <span data-testid="contact-success" className={cx("success")}>
            {successIcon} {ui.thank_you}
          </span>
        )}
        {errorMsg && step !== "success" && (
          <span data-testid="contact-error" className={cx("error")}>{errorMsg}</span>
        )}
      </div>
    </Form>
  );
}
